import React, { useState } from "react";
import { FileDown, Loader } from "lucide-react";
import { useConsumerHouseMoneyTrail } from "../hooks/useTreasury";
import { formatCurrencyAmount } from "../utils/formatCurrencyAmount";
import { downloadCsv } from "../utils/exportCsv";
import MoneyTrailRecords from "./MoneyTrailRecords";

const words = (value) => String(value ?? "").replace(/_/g, " ");

const signed = (value, currency) => {
  const num = Number(value);
  if (Math.abs(num) < 0.005) return formatCurrencyAmount(0, currency);
  return `${num > 0 ? "+" : "-"}${formatCurrencyAmount(Math.abs(num), currency)}`;
};

const tone = (value) => (Math.abs(Number(value)) < 0.005 ? "text-gray-400" : Number(value) < 0 ? "text-red-600" : "text-green-700");

const dayLabel = (date) => new Date(`${date}T12:00:00+01:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

const Heading = ({ children, hint }) => (
  <div className="mb-1">
    <p className="text-xs font-bold tracking-wider text-gray-500 uppercase">{children}</p>
    {hint && <p className="text-xs text-gray-500">{hint}</p>}
  </div>
);

// Where the two sides came apart for one currency: every ledger movement by
// type, then the same by day. Read it top down: the type with the biggest
// Difference is the cause; the day with the biggest movement is when.
function MoneyTrail({ currency }) {
  // Empty = all time. A date = only what moved since that day's position snapshot.
  const [from, setFrom] = useState("");
  const { data, isLoading, isFetching, isError, error } = useConsumerHouseMoneyTrail({ currency, from, enabled: true });
  const [showAllDays, setShowAllDays] = useState(false);
  // The type whose rows are open underneath it.
  const [openCategory, setOpenCategory] = useState(null);

  if (isLoading) {
    return (
      <p className="flex gap-2 items-center text-sm text-gray-500">
        <Loader className="w-4 h-4 animate-spin" /> Reading both ledgers...
      </p>
    );
  }
  if (isError) return <p className="text-sm text-red-600">Could not load the money trail: {error?.message}</p>;

  const { rows, notInLedger, total, gap, daily, from: fromInfo, gapHistory } = data;
  const scope = fromInfo ? `since ${dayLabel(fromInfo.snapshotDate)}` : "all time";
  const newestFirst = [...daily].reverse();
  const biggest = [...daily].sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference)).filter((d) => Math.abs(d.difference) >= 0.005).slice(0, 5);
  const visibleDays = showAllDays ? newestFirst : newestFirst.slice(0, 14);

  // The snapshots, oldest first, each with how far the gap moved since the day
  // before. The first big move is the day the sides drifted apart.
  const history = [...gapHistory]
    .reverse()
    .map((h, i, all) => ({ ...h, change: i === 0 ? null : h.gap - all[i - 1].gap, before: i === 0 ? null : all[i - 1].date }));
  const biggestMove = history.filter((h) => h.change !== null).sort((a, b) => Math.abs(b.change) - Math.abs(a.change))[0];

  const exportTrail = () =>
    downloadCsv(
      `money-trail-${currency}-${fromInfo ? `from-${fromInfo.snapshotDate}` : "all-time"}-${new Date().toISOString().slice(0, 10)}.csv`,
      ["Type", "Consumer records", `Consumer net (${currency})`, "House merchant records", `House merchant net (${currency})`, `Difference (${currency})`],
      [
        ...rows.map((r) => [r.category, r.consumers.count, r.consumers.net, r.houseMerchant.count, r.houseMerchant.net, r.difference]),
        ["Not in the ledger", "", notInLedger.consumers, "", notInLedger.houseMerchant, notInLedger.difference],
        [fromInfo ? "Total (equals the change in the gap)" : "Total (equals the gap)", "", "", "", "", total],
      ],
    );

  return (
    <div className="space-y-5">
      <div className="p-3 bg-white rounded border border-gray-200">
        <div className="flex flex-wrap gap-3 items-center">
          <p className="text-xs font-bold tracking-wider text-gray-500 uppercase">Look from</p>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
            className="px-2 py-1 text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <button
            type="button"
            onClick={() => setFrom("")}
            className={`px-2 py-1 text-xs font-medium rounded-lg ${!from ? "text-white bg-[#013941]" : "text-[#013941] bg-white border border-gray-200"}`}
          >
            All time
          </button>
          {isFetching && <Loader className="w-4 h-4 text-gray-400 animate-spin" />}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          If this currency was in sync on some day, pick that day: the trail then shows only what moved since, starting from that
          day's recorded position. Click a day below to use it.
        </p>
        {fromInfo && (
          <p className="mt-2 text-sm text-gray-800">
            On <strong>{dayLabel(fromInfo.snapshotDate)}</strong> consumers were owed {formatCurrencyAmount(fromInfo.startOwed, currency)} and the house
            merchant held {formatCurrencyAmount(fromInfo.startHeld, currency)} (gap {signed(fromInfo.startGap, currency)}). Now the gap is{" "}
            {signed(gap, currency)}, a change of <strong className={tone(fromInfo.gapChange)}>{signed(fromInfo.gapChange, currency)}</strong>.
            {fromInfo.snapshotDate !== from && ` There was no snapshot on ${dayLabel(from)}; this is the latest one before it.`}
          </p>
        )}
        {history.length > 1 && (
          <div className="mt-3">
            <p className="mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">Gap history (daily snapshots)</p>
            {biggestMove && Math.abs(biggestMove.change) >= 0.005 && (
              <p className="mb-1 text-xs text-gray-600">
                Biggest move between two snapshots: {signed(biggestMove.change, currency)} on {dayLabel(biggestMove.date)}.
              </p>
            )}
            <div className="overflow-auto max-h-48 rounded border border-gray-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="sticky top-0 tracking-wider text-left text-gray-500 uppercase bg-gray-50">
                    <th className="px-3 py-1.5 font-semibold">Snapshot</th>
                    <th className="px-3 py-1.5 font-semibold text-right">Consumers owed</th>
                    <th className="px-3 py-1.5 font-semibold text-right">House merchant held</th>
                    <th className="px-3 py-1.5 font-semibold text-right">Gap</th>
                    <th className="px-3 py-1.5 font-semibold text-right">Change</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[...history].reverse().map((h) => (
                    <tr
                      key={h.date}
                      onClick={() => setFrom(h.date)}
                      title="Look at what moved since this snapshot"
                      className={`cursor-pointer hover:bg-gray-50 ${fromInfo?.snapshotDate === h.date ? "bg-emerald-50" : ""}`}
                    >
                      <td className="px-3 py-1.5 whitespace-nowrap">{dayLabel(h.date)}</td>
                      <td className="px-3 py-1.5 text-right text-gray-700">{formatCurrencyAmount(h.owed, currency)}</td>
                      <td className="px-3 py-1.5 text-right text-gray-700">{formatCurrencyAmount(h.held, currency)}</td>
                      <td className={`px-3 py-1.5 text-right font-semibold ${tone(h.gap)}`}>{signed(h.gap, currency)}</td>
                      <td className={`px-3 py-1.5 text-right ${h.change === null ? "text-gray-300" : tone(h.change)}`}>
                        {h.change === null ? "-" : signed(h.change, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="flex justify-between items-end">
          <Heading
            hint={
              fromInfo
                ? "Every movement since that snapshot, by type, as the ledgers recorded it. The Difference column adds up to the change in the gap."
                : "Every movement on each side, by type, as the ledgers recorded it. The Difference column adds up to the gap."
            }
          >
            {`Where the money moved (${scope})`}
          </Heading>
          <button
            onClick={exportTrail}
            className="flex gap-2 items-center px-3 py-1.5 text-xs font-bold text-[#013941] bg-white rounded-3xl border border-[#013941] hover:bg-gray-50"
          >
            <FileDown className="w-4 h-4" />
            Export CSV
          </button>
        </div>
        <div className="overflow-x-auto bg-white rounded border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs tracking-wider text-left text-gray-500 uppercase bg-gray-50">
                <th className="px-3 py-2 font-semibold">Type</th>
                <th className="px-3 py-2 font-semibold text-right">Consumers</th>
                <th className="px-3 py-2 font-semibold text-right">House merchant</th>
                <th className="px-3 py-2 font-semibold text-right" title="House merchant minus consumers. Negative pulls the house merchant below what consumers hold.">
                  Difference
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <React.Fragment key={r.category}>
                <tr
                  onClick={() => setOpenCategory(openCategory === r.category ? null : r.category)}
                  title="Show the transactions behind this line"
                  className="cursor-pointer hover:bg-gray-50"
                >
                  <td className="px-3 py-2 text-gray-900 capitalize">{words(r.category)}</td>
                  <td className="px-3 py-2 text-right text-gray-700">
                    {r.consumers.count > 0 ? (
                      <>
                        {signed(r.consumers.net, currency)} <span className="text-xs text-gray-400">({r.consumers.count})</span>
                      </>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-700">
                    {r.houseMerchant.count > 0 ? (
                      <>
                        {signed(r.houseMerchant.net, currency)} <span className="text-xs text-gray-400">({r.houseMerchant.count})</span>
                      </>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className={`px-3 py-2 font-semibold text-right ${tone(r.difference)}`}>{signed(r.difference, currency)}</td>
                </tr>
                {openCategory === r.category && (
                  <tr>
                    <td colSpan={4} className="px-3 bg-gray-50/70">
                      <MoneyTrailRecords category={r.category} currency={currency} from={from} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
              <tr
                className="bg-amber-50/40"
                title={
                  fromInfo
                    ? "How much each balance changed since the snapshot beyond what its ledger rows recorded: a change nothing wrote down. It has no date; the SQL pack finds it."
                    : "What each balance holds that no ledger row accounts for: opening balances, reserve, rows written without balances, manual changes. It has no date."
                }
              >
                <td className="px-3 py-2 text-gray-900">Not in the ledger</td>
                <td className="px-3 py-2 text-right text-gray-700">{signed(notInLedger.consumers, currency)}</td>
                <td className="px-3 py-2 text-right text-gray-700">{signed(notInLedger.houseMerchant, currency)}</td>
                <td className={`px-3 py-2 font-semibold text-right ${tone(notInLedger.difference)}`}>{signed(notInLedger.difference, currency)}</td>
              </tr>
              <tr className="font-bold bg-gray-50">
                <td className="px-3 py-2 text-gray-900" colSpan={3}>
                  {fromInfo ? "Total, equals the change in the gap" : "Total, equals the gap"}
                </td>
                <td className={`px-3 py-2 text-right ${tone(total)}`} title={`Gap ${signed(gap, currency)}`}>{signed(total, currency)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <Heading hint={`How much each day pulled the house merchant from consumers, in Nigerian time, since ${dayLabel(data.dailyFrom.slice(0, 10))}. If the balances were in sync before, the first day with a big movement is where it went wrong.`}>
          When it moved
        </Heading>
        {daily.length === 0 ? (
          <p className="text-sm text-gray-500">No ledger movements in this period.</p>
        ) : (
          <>
            {biggest.length > 0 && (
              <p className="mb-2 text-xs text-gray-600">
                Biggest days: {biggest.map((d) => `${dayLabel(d.date)} (${signed(d.difference, currency)})`).join(", ")}
              </p>
            )}
            <div className="overflow-x-auto bg-white rounded border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs tracking-wider text-left text-gray-500 uppercase bg-gray-50">
                    <th className="px-3 py-2 font-semibold">Day</th>
                    <th className="px-3 py-2 font-semibold text-right">Difference</th>
                    <th className="px-3 py-2 font-semibold">What moved it</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visibleDays.map((d) => (
                    <tr key={d.date} className={Math.abs(d.difference) >= 0.005 ? "" : "text-gray-400"}>
                      <td className="px-3 py-2 whitespace-nowrap">{dayLabel(d.date)}</td>
                      <td className={`px-3 py-2 font-semibold text-right ${tone(d.difference)}`}>{signed(d.difference, currency)}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {d.categories.length === 0
                          ? "-"
                          : d.categories.map((c) => `${words(c.category)} ${signed(c.difference, currency)}`).join(" · ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {newestFirst.length > 14 && (
              <button type="button" onClick={() => setShowAllDays((v) => !v)} className="mt-2 text-xs font-medium text-[#013941] hover:underline">
                {showAllDays ? "Show fewer days" : `Show all ${newestFirst.length} days`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default MoneyTrail;
